<!--
Componente: VitrineFilters
Caminho: src/components/VitrineFilters.vue
Descricao: Barra de filtros da vitrine com selecao de secao, categoria,
  marca e ordenacao, adaptada para mobile e desktop.

Interface e Interacao com o Usuario:
  - Botao toggle para expandir ou recolher filtros no mobile
  - Dropdowns para categoria e marca com opcoes traduzidas
  - Radios para secoes e ordenacao conforme disponibilidade

Entrada (Props):
  - `modelValue` (Object) - Estado atual dos filtros
  - `categories` (Array) - Lista de categorias disponiveis
  - `brands` (Array) - Lista de marcas disponiveis
  - `expanded` (Boolean) - Controle do painel em mobile

Saida (Emits):
  - `@update:modelValue` - Dispara ao alterar qualquer filtro
  - `@update:expanded` - Dispara ao alternar expansao no mobile

Dependencias:
  - **Bibliotecas externas:** vue-i18n
-->
<template>
  <div
    class="bg-cinza-200 bg-opacity-100 py-4 px-4 mt-2 rounded text-azul-profundo"
  >
    <!-- Toggle (mobile) -->
    <button
      class="lg:hidden w-full text-left px-4 py-2 text-lg font-semibold flex items-center justify-between"
      @click="$emit('update:expanded', !expanded)"
      :aria-label="$t('vitrine.filters.toggle')"
    >
      {{ $t("vitrine.filters.title") }}
      <svg
        :class="{ 'rotate-180': expanded }"
        class="w-5 h-5 transition-transform"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M19 9l-7 7-7-7"
        />
      </svg>
    </button>

    <div
      :class="{ 'hidden lg:flex': !expanded }"
      class="flex flex-col space-y-4 lg:flex-row lg:space-y-0 lg:items-center lg:justify-between px-4 lg:px-0"
    >
      <div
        class="flex flex-col space-y-4 lg:flex-row lg:flex-wrap lg:space-y-0 lg:gap-4 lg:items-center flex-1"
      >
        <div
          class="flex flex-col space-y-2 lg:flex-row lg:space-y-0 lg:space-x-4"
        >
          <select
            class="p-2 rounded border bg-cinza-claro w-full lg:w-auto"
            :value="modelValue.category"
            @change="updateField('category', $event.target.value)"
            :aria-label="$t('vitrine.filters.category')"
          >
            <option value="Alle">
              {{ $t("vitrine.filters.allCategories") }}
            </option>
            <option value="Nicht angegeben">
              {{ $t("vitrine.filters.unassigned") }}
            </option>
            <option v-for="c in sortedCategories" :key="c" :value="c">
              {{ stripCode(c) }}
            </option>
          </select>

          <select
            class="p-2 rounded border bg-cinza-claro w-full lg:w-auto"
            :value="modelValue.brand"
            @change="updateField('brand', $event.target.value)"
            :aria-label="$t('vitrine.filters.brand')"
          >
            <option value="Alle">
              {{ $t("vitrine.filters.allBrands") }}
            </option>
            <option value="Nicht angegeben">
              {{ $t("vitrine.filters.unassigned") }}
            </option>
            <option v-for="b in sortedBrands" :key="b" :value="b">
              {{ b }}
            </option>
          </select>
        </div>

        <!-- Secoes -->
        <div
          class="flex flex-col space-y-2 lg:flex-row lg:space-y-0 lg:space-x-4"
        >
          <label v-for="s in sections" :key="s" class="flex items-center">
            <input
              type="radio"
              class="mr-2"
              :checked="modelValue.section === s"
              @change="updateField('section', s)"
            />
            {{ labelOf(s) }}
          </label>
        </div>
      </div>

      <div
        class="flex flex-col space-y-2 lg:flex-row lg:space-y-0 lg:space-x-3 lg:items-center lg:ml-auto lg:pl-8"
      >
        <label class="flex items-center">
          <input
            type="radio"
            class="mr-2"
            name="vitrine-sort"
            :checked="modelValue.sortBy === 'Name'"
            @change="updateField('sortBy', 'Name')"
          />
          {{ $t("vitrine.filters.sort.name") }}
        </label>
        <label class="flex items-center">
          <input
            type="radio"
            class="mr-2"
            name="vitrine-sort"
            :checked="modelValue.sortBy === 'Preis'"
            @change="updateField('sortBy', 'Preis')"
          />
          {{ $t("vitrine.filters.sort.price") }}
        </label>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from "vue";
import { useI18n } from "vue-i18n";

export default {
  name: "VitrineFilters",
  props: {
    // { section, category, brand, sortBy }
    modelValue: { type: Object, required: true },
    categories: { type: Array, default: () => [] },
    brands: { type: Array, default: () => [] },
    expanded: { type: Boolean, default: false },
  },
  emits: ["update:modelValue", "update:expanded"],
  data() {
    return {
      sections: ["Alle", "Was gibt's Neues", "Schnäppchen", "Sonderangebote"],
    };
  },
  setup(props) {
    const { t } = useI18n();
    const labelOf = (s) => {
      if (s === "Alle") return t("vitrine.sections.all");
      if (s === "Was gibt's Neues") return t("vitrine.sections.news");
      if (s === "Schnäppchen") return t("vitrine.sections.deals");
      if (s === "Sonderangebote") {
        return t("vitrine.sections.specialOffers");
      }
      return s;
    };

    // Remove código numérico de 4 dígitos do início
    // do label para exibição ao cliente.
    const stripCode = (raw) => {
      const s = String(raw || "")
        .trim()
        .toUpperCase();
      return s.replace(/^\d{4}\s+/, "");
    };

    const sortedCategories = computed(() =>
      [...(props.categories || [])].sort((a, b) =>
        String(a || "").localeCompare(String(b || ""))
      )
    );
    const sortedBrands = computed(() =>
      [...(props.brands || [])].sort((a, b) =>
        String(a || "").localeCompare(String(b || ""))
      )
    );
    return {
      t,
      labelOf,
      stripCode,
      sortedCategories,
      sortedBrands,
    };
  },
  methods: {
    updateField(key, value) {
      const next =
        key === "category"
          ? {
              ...this.modelValue,
              section: "Alle",
              category: value,
              brand: "Alle",
              sortBy: "Name",
            }
          : { ...this.modelValue, [key]: value };
      this.$emit("update:modelValue", next);
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
  },
};
</script>
